import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  useCompanySettingsQuery,
  useConfigurationQuery,
  useEnrollmentContextQuery,
  useSubscriberQuery,
  useBenefitsQuery,
  useContentSettingsQuery,
} from 'theSource/queries';
import './scss/HomePage.css';
import {
  AccountsOtherBenefits,
  EnrollmentBox,
  Hero,
  ReportLifeEventBanner,
  TopNav,
  Welcome,
  HelpSearch,
  CoreBenefits,
  HomeToDoList,
  TotalCompensationStatement,
} from './components';
import { HomePageSection } from './components/HomePageSection';
import { BenefitsInformation } from 'features/HomePage/components/BenefitsInfoPageContent/BenefitsInformation';
import { usePageContentQuery } from 'enrollment/queries/usePageContentQuery';
import { isWaitingForEoi } from './utils';
import {
  hasDepenendentDocRequests,
  selectSubscriberDocuments,
  setSubscriberDocuments,
} from 'enrollment/features/ToDoList/services';
import { selectIsAdmin, selectLumosEnabled } from '../../theSource/features';
import { IntegrationMarketPlace } from './components/IntegrationMarketPlace';
import { mapPlanYearDates } from 'features/MyBenefits/utils';
import { useTotalCompensationStatementQuery } from 'queries';
import { shouldShowLifeEvent } from 'enrollment/utils/LifeEventUtils/lifeEventVisibility';
import { useDocumentRequestQuery } from '../../enrollment/queries';

export const HomePage = () => {
  // need to clear out the enrollment context when we reach home page to prevent context carryover
  localStorage.removeItem('enrollmentContext');
  // need to clear out the enrollment Selected Year when we reach home page to prevent context carryover
  localStorage.removeItem('enrollmentSelectedYear');
  const isAdmin = useSelector(selectIsAdmin);
  const { configuration } = useConfigurationQuery();
  const { companySettings } = useCompanySettingsQuery({
    enabled: Boolean(configuration?.apiHost),
  });
  const dispatch = useDispatch();

  const lumosEnabled = useSelector(selectLumosEnabled);
  const lumosApiHost = configuration?.apiHost;
  const homeEnabled =
    companySettings?.data.lumos_home_page_feature_toggle || false;

  const validStatuses = new Set(['not_started', 'complete', 'life_event']);

  const lifeEventStatuses = new Set(['not_started', 'open', 'complete']);

  const { subscriber } = useSubscriberQuery();

  const { enrollmentContext } = useEnrollmentContextQuery({
    enabled: true,
    apiHost: String(lumosApiHost),
  });

  const { pageContent, isFetchingPageContent, isLoadingPageContent } =
    usePageContentQuery({
      pageContentLookupCode: 'dashboard/initial',
    });

  const isLoadingContentBlocks = isFetchingPageContent || isLoadingPageContent;
  const contentBlocks = pageContent?.data.content_blocks;
  const hasContentBlocks =
    contentBlocks &&
    contentBlocks.some(
      (block) => block.icon_image || block.documents.length > 0
    );

  const showLifeEventBanner = shouldShowLifeEvent(enrollmentContext);

  const {
    data: benefitsData,
    isLoading: isLoadingBenefits,
    isFetching: isFetchingBenefits,
  } = useBenefitsQuery({ enabled: true });

  const subscriberDocumentsState = useSelector(selectSubscriberDocuments);

  const hasDocumentRequests =
    (subscriberDocumentsState?.document_requests.subscriber || []).length > 0 ||
    hasDepenendentDocRequests(
      subscriberDocumentsState?.document_requests.dependents
    );

  const hasWaitingForEoi = isWaitingForEoi(benefitsData);
  const showToDoList = hasDocumentRequests || hasWaitingForEoi;
  const dates = mapPlanYearDates(benefitsData?.data.current);
  let associatedContexts = false;

  // don't show benefits if there is a NH or OE context in progress for current plan year
  // if there are no contexts that means they have closed so cannot be open
  const hasAvailableContexts = enrollmentContext?.data.filter((enrollment) =>
    dates.opyIds?.has(enrollment.org_plan_year_id)
  );

  if (!hasAvailableContexts || hasAvailableContexts.length === 0) {
    associatedContexts = true;
  }

  const hasInProgressContexts = hasAvailableContexts?.filter(
    (enrollment) =>
      enrollment.enrollment_type_lookup_code === 'life_event' ||
      !['started', 'not_started'].includes(enrollment.status)
  );
  if (!associatedContexts) {
    associatedContexts = Boolean(
      hasInProgressContexts && hasInProgressContexts.length > 0
    );
  }

  const { contentSettings } = useContentSettingsQuery({
    enabled: Boolean(configuration?.apiHost),
  });

  const totalCompensationEnabled =
    contentSettings?.total_compensation_statements || false;

  const { data: totalCompensationData } = useTotalCompensationStatementQuery({
    enabled: totalCompensationEnabled,
  });

  const showTotalCompensationStatement =
    totalCompensationEnabled &&
    Boolean(totalCompensationData?.data) &&
    Boolean(totalCompensationData?.data.actionSequence?.length);

  const context = enrollmentContext?.data?.find(
    (context) =>
      context.org_plan_year_id !== 0 &&
      ((validStatuses.has(context.status) &&
        context.enrollment_type_lookup_code !== 'life_event') ||
        (lifeEventStatuses.has(context.status) &&
          context.enrollment_type_lookup_code === 'life_event'))
  );

  // Call the hook unconditionally at the top level
  const { documentRequestsData } = useDocumentRequestQuery({
    lumosEnabled: Boolean(lumosEnabled || homeEnabled),
    orgPlanYearId: context?.org_plan_year_id || undefined,
    enrollmentContextValue: context?.enrollment_type_lookup_code || undefined,
  });

  useEffect(() => {
    if (context && documentRequestsData) {
      dispatch(setSubscriberDocuments(documentRequestsData?.data));
    }
  }, [context, documentRequestsData, dispatch]);

  return (
    <main className="home" id="mainContent">
      <div className="welcomeInfo">
        <Hero contexts={enrollmentContext}>
          <div className="homeContent">
            <EnrollmentBox contexts={enrollmentContext} />
            <Welcome subscriber={subscriber} />
            {companySettings?.data.chat_bot && !isAdmin && <HelpSearch />}
          </div>
        </Hero>
      </div>
      <div id="enrollmentInfo" className="enrollmentInfo">
        <div className="homeContent">
          <div className="order-md-1 order-3 pt-4 pt-md-0">
            <TopNav />
          </div>

          {showToDoList && lumosEnabled && (
            <HomePageSection
              additionalClasses="order-md-2 order-1"
              rowVerticalAlign="center"
            >
              <HomeToDoList
                benefitsData={benefitsData}
                documentRequestsData={documentRequestsData}
              />
            </HomePageSection>
          )}

          {showLifeEventBanner && (
            <HomePageSection additionalClasses="order-md-3 order-5 le-banner">
              <ReportLifeEventBanner />
            </HomePageSection>
          )}

          {hasContentBlocks && (
            <div className="benefitInfo row m-0">
              <div className="homeContent">
                <HomePageSection additionalClasses="mt-0 benefits-info-section">
                  <div
                    id="benefits-info-container"
                    className="col-4 col-md-8 col-lg-12 px-0"
                  >
                    <BenefitsInformation
                      contentBlocks={contentBlocks}
                      isLoading={isLoadingContentBlocks}
                    />
                  </div>
                </HomePageSection>
              </div>
            </div>
          )}

          {associatedContexts && (
            <>
              <CoreBenefits
                benefitsData={benefitsData?.data}
                isLoading={isLoadingBenefits}
                isFetching={isFetchingBenefits}
              />

              <AccountsOtherBenefits />
            </>
          )}

          {companySettings?.data?.integration_marketplace_enabled && (
            <IntegrationMarketPlace />
          )}

          {showTotalCompensationStatement && (
            <TotalCompensationStatement
              apiHost={lumosApiHost}
              totalCompensationData={totalCompensationData?.data}
            />
          )}
        </div>
      </div>
    </main>
  );
};
